import type { SearchTableQuery } from "owa-mail-list-search";
import { getTableItemRelation, MailRowDataPropertyGetter, type MailListRowDataType, type TableView } from "owa-mail-list-store";
import TableOperations from "owa-mail-list-table-operations";
import { getTableViewFromTableQuery } from "owa-mail-triage-table-utils";
import type ReactListViewType from "owa-service/lib/contract/ReactListViewType";
import { mutatorAction } from "satcheljs";

const setPreservedQueryOnTypeRowsMutator = mutatorAction("setPreservedQueryOnTypeRows", (tableView: TableView, recentRowKeys: string[], recentReferenceKeys: string[], moreResultsRowKeys: string[], highlightTerms: string[], highlightAccuracy: TableView["highlightAccuracy"]): void => {
    tableView.recentRowKeys = recentRowKeys;
    tableView.recentReferenceKeys = recentReferenceKeys;
    tableView.moreResultsRowKeys = moreResultsRowKeys;
    tableView.hasPreservedQueryOnTypeRows =
        recentRowKeys.length > 0 || moreResultsRowKeys.length > 0;
    tableView.hasPreservedQueryOnTypeRecentRows = recentRowKeys.length > 0;
    tableView.highlightTerms = highlightTerms;
    tableView.highlightAccuracy = highlightAccuracy;
});

export default function preserveQueryOnTypeRows(sourceTableView: TableView | undefined, submittedTableQuery: SearchTableQuery): void {
    const sourceTableQuery = sourceTableView?.tableQuery as SearchTableQuery | undefined;
    const hasQueryOnTypeRows = sourceTableQuery?.scenarioType === "queryOnType" ||
        (sourceTableQuery?.scenarioType === "mail" &&
            (sourceTableView?.hasPreservedQueryOnTypeRows === true ||
                sourceTableView?.hasPreservedQueryOnTypeRecentRows === true));
    const recentRowKeys = sourceTableView?.recentRowKeys ?? [];
    const moreResultsRowKeys = sourceTableView?.moreResultsRowKeys ?? [];
    const shouldHideOriginalQueryOnTypeRows = sourceTableView?.isOriginalQueryOnTypeSectionRedesignEnabled === true &&
        submittedTableQuery.searchSortOrder !== undefined &&
        submittedTableQuery.searchSortOrder !== "Hybrid";
    if (!sourceTableView ||
        !hasQueryOnTypeRows ||
        shouldHideOriginalQueryOnTypeRows ||
        submittedTableQuery.scenarioType !== "mail" ||
        sourceTableQuery.queryString !== submittedTableQuery.queryString ||
        sourceTableQuery.listViewType !== 1 ||
        submittedTableQuery.listViewType !== 1 ||
        (recentRowKeys.length === 0 && moreResultsRowKeys.length === 0)) {
        return;
    }
    
    const submittedTableView = getTableViewFromTableQuery(submittedTableQuery);
    if (submittedTableView.rowKeys.length > 0) {
        return;
    }
    
    const copyRows = (rowKeys: string[], referenceKeys?: string[]): {
        rowKeys: string[];
        referenceKeys: string[];
    } => {
        const copiedRowKeys: string[] = [];
        const copiedReferenceKeys: string[] = [];
        
        for (const [index, rowKey] of rowKeys.entries()) {
            const item = MailRowDataPropertyGetter.getItem(rowKey, sourceTableView);
            if (!item) {
                continue;
            }
            
            const relation = getTableItemRelation(rowKey, sourceTableView.id);
            if (!relation) {
                continue;
            }
            
            const itemReferenceKey = "ReferenceKey" in item && typeof item.ReferenceKey === "string"
                ? item.ReferenceKey
                : undefined;
            const referenceKey = referenceKeys?.[index] ?? itemReferenceKey;
            const row: MailListRowDataType = {
                ...item,
                __typename: "ItemRow",
                id: relation.clientId.Id,
                ItemId: relation.clientId,
                InstanceKey: rowKey,
                SortOrderSource: relation.executeSearchSortOrder,
                ReferenceKey: referenceKey,
            } as MailListRowDataType;
            
            if (TableOperations.addRowMutator(submittedTableView.rowKeys.length, row, submittedTableView, "", false, "SearchMessageSuggestion")) {
                copiedRowKeys.push(rowKey);
                if (referenceKey) {
                    copiedReferenceKeys.push(referenceKey);
                }
            }
        }
        
        return { rowKeys: copiedRowKeys, referenceKeys: copiedReferenceKeys };
    };
    
    const preservedRecentRows = copyRows(recentRowKeys, sourceTableView.recentReferenceKeys);
    const preservedMoreResultsRows = copyRows(moreResultsRowKeys);
    
    if (preservedRecentRows.rowKeys.length > 0 || preservedMoreResultsRows.rowKeys.length > 0) {
        setPreservedQueryOnTypeRowsMutator(submittedTableView, preservedRecentRows.rowKeys, preservedRecentRows.referenceKeys, preservedMoreResultsRows.rowKeys, sourceTableView.highlightTerms ?? [], sourceTableView.highlightAccuracy);
    }
}
